<template>
  <v-container class="password-page py-6" fluid>
    <v-row justify="center">
      <v-col cols="12" sm="9" md="7" lg="6" xl="5">
        <v-card class="password-card" elevation="2">
          <!-- Header -->
          <div class="password-header">
            <div class="password-title-block">
              <div class="password-title-icon">
                <v-icon color="primary" size="28">
                  mdi-lock-reset
                </v-icon>
              </div>

              <div>
                <div class="text-h6 font-weight-bold">
                  Neues Passwort festlegen
                </div>

                <div class="text-body-2 text-medium-emphasis">
                  Legen Sie ein neues Passwort für Ihr Benutzerkonto fest.
                </div>
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="password-card-content">
            <v-alert
              type="info"
              variant="tonal"
              border="start"
              class="password-hint"
            >
              Das neue Passwort muss mindestens acht Zeichen lang sein.
            </v-alert>

            <v-form
              ref="formRef"
              v-model="valid"
              class="password-form"
              @submit.prevent="submit"
            >
              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-lock-plus-outline
                  </v-icon>

                  <span>Neues Passwort</span>
                </div>

                <v-text-field
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  :rules="[requiredRule, minLengthRule]"
                  :error-messages="fieldErrors.new_password"
                  label="Neues Passwort"
                  autocomplete="new-password"
                  prepend-inner-icon="mdi-lock-plus-outline"
                  :append-inner-icon="
                    showPassword ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-3"
                  @click:append-inner="showPassword = !showPassword"
                  @update:model-value="clearFieldError('new_password')"
                />

                <v-text-field
                  v-model="password2"
                  :type="showPassword2 ? 'text' : 'password'"
                  :rules="[requiredRule, minLengthRule, matchRule]"
                  :error-messages="fieldErrors.confirm_password"
                  label="Neues Passwort wiederholen"
                  autocomplete="new-password"
                  prepend-inner-icon="mdi-lock-check-outline"
                  :append-inner-icon="
                    showPassword2 ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :disabled="saving"
                  validate-on="blur"
                  class="mb-1"
                  @click:append-inner="showPassword2 = !showPassword2"
                  @update:model-value="clearFieldError('confirm_password')"
                />
              </div>
            </v-form>
          </v-card-text>

          <v-divider />

          <div class="password-footer">
            <v-btn
              variant="text"
              :disabled="saving"
              @click="goToLogin"
            >
              Abbrechen
            </v-btn>

            <v-btn
              color="primary"
              variant="elevated"
              :loading="saving"
              :disabled="!valid || saving"
              class="submit-button"
              @click="submit"
            >
              <v-icon start>mdi-check</v-icon>
              Passwort speichern
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/plugins/axios'
import { useProjectStore } from '@/stores/projectStore'

const router = useRouter()
const store = useProjectStore()

const formRef = ref(null)
const valid = ref(false)
const saving = ref(false)

const password = ref('')
const password2 = ref('')

const showPassword = ref(false)
const showPassword2 = ref(false)

const fieldErrors = ref({
  new_password: [],
  confirm_password: [],
})

const requiredRule = value =>
  !!value || 'Dieses Feld muss ausgefüllt sein.'

const minLengthRule = value =>
  !value ||
  value.length >= 8 ||
  'Das Passwort muss mindestens 8 Zeichen lang sein.'

const matchRule = value =>
  value === password.value ||
  'Die Passwörter stimmen nicht überein.'

function clearFieldError(field) {
  fieldErrors.value[field] = []
}

function normalizeErrors(value) {
  if (!value) return []
  return Array.isArray(value) ? value : [String(value)]
}

function resetFieldErrors() {
  fieldErrors.value = {
    new_password: [],
    confirm_password: [],
  }
}

function goToLogin() {
  store.forgotPassword = false

  if (router.hasRoute('Login')) {
    router.replace({ name: 'Login' })
  } else {
    router.replace({ name: 'Home' })
  }
}

async function submit() {
  if (saving.value) return

  const result = await formRef.value?.validate?.()

  if (!result?.valid) {
    toast.warning('Bitte überprüfen Sie Ihre Eingaben.')
    return
  }

  resetFieldErrors()
  saving.value = true

  const payload = {
    new_password: password.value,
    confirm_password: password2.value,
  }

  try {
    await api.post(
      '/auth/login/forgot-password/',
      payload
    )

    toast.success('Passwort wurde erfolgreich geändert.')

    store.forgotPassword = false

    await store.checkSession()

    router.replace({ name: 'Home' })
  } catch (error) {
    const status = error?.response?.status
    const data = error?.response?.data || {}

    if (status === 400) {
      fieldErrors.value.new_password = normalizeErrors(
        data.new_password
      )

      fieldErrors.value.confirm_password = normalizeErrors(
        data.confirm_password
      )

      if (data.confirm_password) {
        toast.error('Die Passwörter stimmen nicht überein.')
      }

      const nonFieldErrors = normalizeErrors(
        data.non_field_errors
      )

      nonFieldErrors.forEach(message => {
        toast.error(message)
      })

      if (
        !data.new_password &&
        !data.confirm_password &&
        !data.non_field_errors
      ) {
        toast.error('Bitte überprüfen Sie Ihre Eingaben.')
      }
    } else {
      toast.error(
        'Das Passwort konnte nicht geändert werden.'
      )
    }
  } finally {
    saving.value = false
  }
}
</script>

<style scoped lang="scss">
.password-page {
  min-height: 100%;
}

.password-card {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 28px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

.password-header {
  padding: 1.25rem 1.5rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.password-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.password-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

.password-card-content {
  display: grid;
  gap: 1.25rem;
  padding: 1.5rem;
}

.password-hint {
  border-radius: 20px;
}

.password-form {
  display: grid;
  gap: 1.25rem;
}

.form-section {
  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.form-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

.password-form :deep(.v-field) {
  border-radius: 18px;
}

.password-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;

  padding: 1rem 1.5rem;
}

.password-footer .v-btn {
  font-weight: 600;
  text-transform: none;
}

.submit-button {
  min-width: 180px;
}

@media (max-width: 700px) {
  .password-page {
    padding-inline: 0.5rem;
  }

  .password-header {
    padding: 1rem;
  }

  .password-title-block {
    align-items: flex-start;
  }

  .password-title-icon {
    width: 42px;
    height: 42px;
  }

  .password-card-content {
    padding: 1rem;
  }

  .form-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .password-footer {
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 1rem;
  }

  .password-footer .v-btn {
    width: 100%;
  }

  .submit-button {
    min-width: 0;
  }
}
</style>